import type { CSSProperties } from 'react'
import type { CredentialState } from '../lib/types'

// ─────────────────────────────────────────────────────────────────────────
// The Vouchr brand mark — the Ghost, ported verbatim from the shipped
// favicon/landing page. This is the single source of truth for the mark.
//
//  - `size` controls render size.
//  - `tint` recolors the rounded-square background to a credential state so
//    the same mark doubles as a verification-tier badge.
//  - `breathing` adds the gentle live-pulse used on "live verified" elements.
//  - `gaze` shifts the pupils slightly (used for personality in empty states).
// ─────────────────────────────────────────────────────────────────────────

const TINT: Record<CredentialState, string> = {
  live: 'var(--cred-live)',
  founding: 'var(--cred-founding)',
  vault: 'var(--cred-vault)',
  passive: 'var(--cred-passive)',
  stale: 'var(--cred-stale)',
}

interface Props {
  size?: number
  /** Recolor the body background to a credential state. Defaults to brand indigo. */
  tint?: CredentialState
  breathing?: boolean
  glow?: boolean
  gaze?: { x: number; y: number }
  className?: string
  style?: CSSProperties
  title?: string
}

export function GhostMark({
  size = 40,
  tint,
  breathing = false,
  glow = false,
  gaze = { x: 0, y: 0 },
  className,
  style,
  title = 'Vouchr',
}: Props) {
  const bg = tint ? TINT[tint] : 'var(--c-primary)'
  // On colored tints the eyes/sockets read better as the deep background.
  const eyeFill = tint && tint !== 'passive' ? 'var(--c-deepest)' : '#3c3489'

  return (
    <svg
      viewBox="0 0 1024 1024"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      className={className}
      style={{
        display: 'block',
        borderRadius: '22%',
        overflow: 'visible',
        filter: glow
          ? 'drop-shadow(0 0 10px var(--c-glow-1)) drop-shadow(0 6px 18px rgba(11,9,26,0.55))'
          : undefined,
        animation: breathing ? 'float 8s ease-in-out infinite' : undefined,
        ...style,
      }}
    >
      <title>{title}</title>
      <rect width="1024" height="1024" rx="224" fill={bg} />
      <path
        d="M192 640 C192 448 320 320 512 320 C704 320 832 448 832 640 L832 800 C808 800 784 830 760 800 C736 770 712 830 688 800 C664 770 640 830 616 800 C592 770 568 830 544 800 C520 770 496 800 472 800 C448 800 192 800 192 800 Z"
        fill="white"
      />
      <ellipse cx="368" cy="608" rx="80" ry="90" fill={eyeFill} />
      <ellipse cx="656" cy="608" rx="80" ry="90" fill={eyeFill} />
      <circle cx="384" cy="630" r="44" fill="white" />
      <circle cx="672" cy="630" r="44" fill="white" />
      <circle cx={400 + gaze.x} cy={608 + gaze.y} r="18" fill={eyeFill} />
      <circle cx={688 + gaze.x} cy={608 + gaze.y} r="18" fill={eyeFill} />
    </svg>
  )
}
